import React, { Fragment, useState } from "react";
import { useTheme } from "styled-components";
import { formatDateTimeWithTime, getUserName } from "../../../../util/util";
import type {
  ResultItem,
  ActivityHistoryValue,
  RelatedEntitiesValue,
  CompanyHierarchyValue,
} from "./types";
import type {
  Assets,
  AttributeInput,
  AttributeObjectType,
  AttributeSchema,
  AttributeValue,
  ObjectReferenceResult,
  ProductReference,
} from "../../../../types/types.PIM";
import {
  SubItemContainer,
  SubItemHeader,
  SubItemContent,
  CaretWrapper,
  FlexDiv,
  DateTime,
  GroupTitle,
  SubItemCard,
  ModifiedText,
  DiffText,
  LinkAttr,
  FixedCardPreview,
} from "./styles";
import { CaretDownIcon } from "../../../../components/Icons/Icons";
import { ColumnDef } from "@tanstack/react-table";
import { Table } from "../../../../components/Table/Table";
import { formatDate } from "../../../../util/util";
import { AttributeValueDisplay } from "../ProductOverview/product_overview.utils";
import {
  computeSmartDiff,
  EMPTY_VALUE,
  isNullOrUndefined,
  isActivityHistoryValue,
  hasValueProperty,
  isString,
  getFirstElement,
  renderHtmlWithDiff,
} from "./utils";
import { useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useRoutePath } from "../../../../util/Routing";
import { HistoryProductCard } from "../../../../components/CardProduct/HistoryProductCard";
import { getObjectReferenceDetailPath } from "../../../../components/ObjectReference/ObjectReferencePreviewTooltip";
import { LogTimeline } from "../../../../components/Logs/LogTimeline";
import { User } from "../../../../types/types";
import { useTranslation } from "react-i18next";
import { renderChip } from "./Attributes";
import {
  AddressMode,
  prepareAddressForForm,
} from "../../../admin/SellerAdmin/PIM/SellerAdminPIMAttributes/AddressAttributes/address.utils";
import { getAddressFieldsByMode } from "./AddressTable";
import { spacingTokens } from "../../../../designTokens";

interface CollectionItemProps {
  results: ResultItem[];
  suppressHeader?: boolean;
}

type CollectionValue =
  | ActivityHistoryValue
  | ActivityHistoryValue[]
  | Assets
  | RelatedEntitiesValue
  | CompanyHierarchyValue
  | null
  | undefined;

function convertToAttributeRows(subItems: ResultItem["subItems"]): Array<{
  name: string;
  beforeValue: CollectionValue;
  afterValue: CollectionValue;
  inputType: string;
  isMultiple: boolean;
  isAddressField?: boolean;
  addressMode?: AddressMode;
}> {
  return subItems.map((subItem) => ({
    name: subItem.attributeName,
    beforeValue: subItem.beforeValue,
    afterValue: subItem.afterValue,
    inputType: subItem.inputType,
    isMultiple: subItem.isMultiple ?? false,
    addressMode: subItem.addressMode,
  }));
}

export function CollectionItem({
  results,
  suppressHeader = false,
}: CollectionItemProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedOuterRows, setExpandedOuterRows] = useState<Set<string>>(
    new Set()
  );
  const [expandedInnerRows, setExpandedInnerRows] = useState<Set<string>>(
    new Set()
  );

  const first = results[0];

  const toggleInnerRow = (rowId: string) => {
    setExpandedInnerRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const toggleOuterRow = (rowId: string) => {
    setExpandedOuterRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const handleToggle = () => {
    setIsExpanded((prev) => !prev);
  };

  if (suppressHeader) {
    const isOuterExpanded = expandedOuterRows.has(results[0].id);
    const handleOuterRowClick = () => {
      if (isOuterExpanded) {
        setExpandedInnerRows((prev) => {
          const next = new Set(prev);
          results.forEach((r) => next.delete(r.id));
          return next;
        });
      }
      toggleOuterRow(results[0].id);
    };
    return (
      <>
        <SubItemCard key={results[0].id}>
          <SubItemHeader onClick={handleOuterRowClick}>
            <CaretWrapper isExpanded={isOuterExpanded}>
              <CaretDownIcon
                height={16}
                width={16}
                fill={theme.secondaryTextColor}
              />
            </CaretWrapper>
            <ModifiedText>
              <b>{results[0].targetName}</b>
            </ModifiedText>
          </SubItemHeader>
          {isOuterExpanded &&
            results.map((result: ResultItem) => {
              const isInnerExpanded = expandedInnerRows.has(result.id);
              return (
                <SubItemContent key={result.id}>
                  <SubItemContainer>
                    <SubItemHeader onClick={() => toggleInnerRow(result.id)}>
                      <CaretWrapper isExpanded={isInnerExpanded}>
                        <CaretDownIcon
                          height={16}
                          width={16}
                          fill={theme.secondaryTextColor}
                        />
                      </CaretWrapper>
                      <FlexDiv>
                        <ModifiedText>
                          {result.operation} <b>Item</b>
                        </ModifiedText>
                        <DateTime>
                          {formatDateTimeWithTime(result.modified_at)}
                        </DateTime>
                      </FlexDiv>
                    </SubItemHeader>
                    {isInnerExpanded && (
                      <SubItemContent>
                        <CollectionTableComponent
                          attributes={convertToAttributeRows(result.subItems)}
                        />
                      </SubItemContent>
                    )}
                  </SubItemContainer>
                </SubItemContent>
              );
            })}
        </SubItemCard>
      </>
    );
  }

  const message = (
    <>
      {t("Modified")} <b>{first.targetName}</b>
    </>
  );

  const children = (
    <>
      <GroupTitle>
        <b>{first.targetName}</b>
      </GroupTitle>
      {results.map((result: ResultItem) => {
        const isInnerExpanded = expandedInnerRows.has(result.id);

        return (
          <SubItemContainer key={result.id}>
            <SubItemHeader onClick={() => toggleInnerRow(result.id)}>
              <CaretWrapper isExpanded={isInnerExpanded}>
                <CaretDownIcon
                  height={16}
                  width={16}
                  fill={theme.secondaryTextColor}
                />
              </CaretWrapper>
              <FlexDiv>
                <ModifiedText>
                  {result.operation} <b>Item</b>
                </ModifiedText>
                <DateTime>
                  {formatDateTimeWithTime(result.modified_at)}
                </DateTime>
              </FlexDiv>
            </SubItemHeader>
            {isInnerExpanded && (
              <SubItemContent>
                <CollectionTableComponent
                  attributes={convertToAttributeRows(result.subItems)}
                />
              </SubItemContent>
            )}
          </SubItemContainer>
        );
      })}
    </>
  );

  return (
    <LogTimeline
      id={first.id}
      username={first.last_modified_by}
      date={first.modified_at}
      message={message}
      isExpanded={isExpanded}
      children={children}
      onToggle={handleToggle}
    />
  );
}

type InputType =
  | "product_reference"
  | "object_reference"
  | "user"
  | "multi_select"
  | "single_select"
  | "date"
  | "numeric"
  | "multiline"
  | "multiline_entry"
  | "html"
  | string;

interface CollectionAttributeValueDisplayProps {
  value: CollectionValue;
  inputType: InputType;
  attributeName: string;
  isPortfolio?: boolean;
  backgroundColor?: string;
  beforeValue?: CollectionValue;
  afterValue?: CollectionValue;
  column?: "before" | "after";
}

interface AttributeRow {
  name: string;
  beforeValue: CollectionValue;
  afterValue: CollectionValue;
  inputType: InputType;
  isMultiple: boolean;
  isAddressField?: boolean;
  addressMode?: AddressMode;
}

function extractHtmlString(value: CollectionValue): string {
  if (isNullOrUndefined(value)) return "";
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0];
    if (isActivityHistoryValue(first) && isString(first.value)) {
      return first.value;
    }
  }
  if (isActivityHistoryValue(value) && isString(value.value)) {
    return value.value;
  }
  return "";
}

function CollectionTableComponent({
  attributes,
}: {
  attributes: Array<AttributeRow>;
}) {
  const { t } = useTranslation();

  const expandedAttributes = useMemo(() => {
    return attributes.flatMap((row) => {
      if (row.inputType !== "address") return [row];
      const addressFields = getAddressFieldsByMode(t, row.addressMode);

      const beforeAddr = isActivityHistoryValue(row.beforeValue)
        ? prepareAddressForForm(row.beforeValue.value as Record<string, any>)
        : null;
      const afterAddr = isActivityHistoryValue(row.afterValue)
        ? prepareAddressForForm(row.afterValue.value as Record<string, any>)
        : null;

      const getVal = (addr: Record<string, any> | null, key: string) => {
        const val = addr?.[key];
        if (!val) return null;
        const display =
          typeof val === "object" && "label" in val ? val.label : String(val);
        return {
          attribute_id: key,
          data_type: "text",
          id: key,
          priority: 0,
          value: display,
        } as ActivityHistoryValue;
      };

      return addressFields.map(({ label, key }) => ({
        name: label,
        beforeValue: getVal(beforeAddr, key),
        afterValue: getVal(afterAddr, key),
        inputType: "text",
        isMultiple: false,
        isAddressField: true,
      }));
    });
  }, [attributes, t]);

  const formatValue = (value: CollectionValue, type: InputType): string => {
    if (isNullOrUndefined(value)) return EMPTY_VALUE;

    if (hasValueProperty(value)) {
      const val = value as ActivityHistoryValue;
      const stringValue = val.value?.toString() ?? null;

      if (type === "date" && stringValue) {
        try {
          return formatDate(stringValue);
        } catch {
          return stringValue;
        }
      }
      if (type === "numeric" && typeof val.value === "number") {
        return val.value.toString();
      }
      return stringValue ?? EMPTY_VALUE;
    }

    if (Array.isArray(value) && value.length > 0) {
      const first = getFirstElement(value, isActivityHistoryValue);
      if (first) {
        return formatValue(first, type);
      }
    }

    return EMPTY_VALUE;
  };

  const valuesAreDifferent = (
    beforeVal: CollectionValue,
    afterVal: CollectionValue,
    type: InputType
  ): boolean => {
    if (isNullOrUndefined(beforeVal) && isNullOrUndefined(afterVal)) {
      return false;
    }
    if (isNullOrUndefined(beforeVal) || isNullOrUndefined(afterVal)) {
      return true;
    }
    return formatValue(beforeVal, type) !== formatValue(afterVal, type);
  };

  const columns: ColumnDef<AttributeRow>[] = [
    {
      accessorKey: "name",
      header: "Attribute Name",
      cell: ({ getValue }) => (
        <div
          style={{
            fontWeight: "var(--font_weight_regular, 400)",
            textAlign: "left",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            verticalAlign: "top",
            alignSelf: "flex-start",
          }}
        >
          {getValue() as string}
        </div>
      ),
    },
    {
      accessorKey: "beforeValue",
      header: "Before",
      cell: ({ row }) => {
        const value = row.original.beforeValue;
        const inputType = row.original.inputType;
        const beforeValue = row.original.beforeValue;
        const afterValue = row.original.afterValue;
        const hasChange = valuesAreDifferent(
          beforeValue,
          afterValue,
          inputType
        );

        if (value == null) {
          return (
            <div
              style={{
                color: "var(--color_text_tertiary, #9ca3af)",
                fontStyle: "italic",
                textAlign: "left",
              }}
            >
              {EMPTY_VALUE}
            </div>
          );
        }

        if (row.original.isAddressField) {
          return (
            <DiffText removed={hasChange}>
              {formatValue(value, inputType)}
            </DiffText>
          );
        }

        return (
          <div
            style={{
              textAlign: "left",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              verticalAlign: "top",
              alignSelf: "flex-start",
            }}
          >
            {inputType === "date" && hasChange ? (
              <>
                {computeSmartDiff(
                  formatValue(row.original.beforeValue, inputType),
                  formatValue(row.original.afterValue, inputType)
                )
                  .filter((part) => !part.added)
                  .map((part, index) => (
                    <DiffText key={`before-${index}`} removed={part.removed}>
                      {part.value}
                    </DiffText>
                  ))}
              </>
            ) : (inputType === "html" ||
                inputType === "multiline" ||
                inputType === "multiline_entry") &&
              hasChange ? (
              <>
                {renderHtmlWithDiff(
                  extractHtmlString(beforeValue),
                  extractHtmlString(afterValue),
                  true
                )}
              </>
            ) : (
              <CollectionAttributeValueDisplay
                value={value}
                inputType={inputType}
                attributeName={row.original.name}
                beforeValue={beforeValue}
                afterValue={afterValue}
                column="before"
                backgroundColor={
                  hasChange && !row.original.isMultiple
                    ? "var(--color_bg_surface_error_diff)"
                    : undefined
                }
              />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "afterValue",
      header: "After",
      cell: ({ row }) => {
        const value = row.original.afterValue;
        const inputType = row.original.inputType;
        const beforeValue = row.original.beforeValue;
        const afterValue = row.original.afterValue;
        const hasChange = valuesAreDifferent(
          beforeValue,
          afterValue,
          inputType
        );

        if (value == null) {
          return (
            <div
              style={{
                color: "var(--color_text_tertiary, #9ca3af)",
                fontStyle: "italic",
                textAlign: "left",
              }}
            >
              {EMPTY_VALUE}
            </div>
          );
        }

        if (row.original.isAddressField) {
          return (
            <DiffText added={hasChange}>
              {formatValue(value, inputType)}
            </DiffText>
          );
        }

        return (
          <div
            style={{
              textAlign: "left",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              verticalAlign: "top",
              alignSelf: "flex-start",
            }}
          >
            {inputType === "date" && hasChange ? (
              <>
                {computeSmartDiff(
                  formatValue(row.original.beforeValue, inputType),
                  formatValue(row.original.afterValue, inputType)
                )
                  .filter((part) => !part.removed)
                  .map((part, index) => (
                    <DiffText key={`after-${index}`} added={part.added}>
                      {part.value}
                    </DiffText>
                  ))}
              </>
            ) : (inputType === "html" ||
                inputType === "multiline" ||
                inputType === "multiline_entry") &&
              hasChange ? (
              <>
                {renderHtmlWithDiff(
                  extractHtmlString(beforeValue),
                  extractHtmlString(afterValue),
                  false
                )}
              </>
            ) : (
              <CollectionAttributeValueDisplay
                value={value}
                inputType={inputType}
                attributeName={row.original.name}
                beforeValue={beforeValue}
                afterValue={afterValue}
                column="after"
                backgroundColor={
                  hasChange && !row.original.isMultiple
                    ? "var(--color_bg_surface_success_diff)"
                    : undefined
                }
              />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Table
      columns={columns}
      data={expandedAttributes}
      isLoading={false}
      error={undefined}
      enableDefaultSort={false}
      enableColumnResize={false}
      lastChildleftAlign={true}
      cellVerticalAlignTop={true}
    />
  );
}

function CollectionAttributeValueDisplay({
  value,
  inputType,
  attributeName,
  isPortfolio = false,
  backgroundColor,
  beforeValue,
  afterValue,
  column,
}: CollectionAttributeValueDisplayProps): React.ReactElement | null {
  const theme = useTheme();
  const { adminPath } = useRoutePath();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [coords, setCoords] = useState<
    Record<number, { top: number; left: number }>
  >({});
  const calcFixedCoords = useCallback(calcFixedCoordsOfCard, []);

  const values: ActivityHistoryValue[] = useMemo(() => {
    if (isNullOrUndefined(value)) return [];

    if (Array.isArray(value)) {
      return value.filter(isActivityHistoryValue);
    }

    if (isActivityHistoryValue(value)) {
      return [value];
    }

    return [];
  }, [value]);

  if (inputType === "product_reference" && values.length > 0) {
    const extractProducts = (val: CollectionValue): ProductReference[] => {
      if (isNullOrUndefined(val)) return [];
      if (Array.isArray(val)) {
        return val
          .filter(isActivityHistoryValue)
          .map((v) => v.product_ref)
          .filter((ref): ref is ProductReference => ref !== undefined);
      }
      if (isActivityHistoryValue(val)) {
        return val.product_ref ? [val.product_ref] : [];
      }
      return [];
    };

    const beforeProducts = extractProducts(beforeValue);
    const afterProducts = extractProducts(afterValue);

    const showRemovedFn = (p: ProductReference) =>
      column === "before" && !afterProducts.some((ap) => ap.id === p.id);
    const showAddedFn = (p: ProductReference) =>
      column === "after" && !beforeProducts.some((bp) => bp.id === p.id);

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: spacingTokens.spacing_md,
        }}
      >
        {values
          .map((v: ActivityHistoryValue) => v.product_ref)
          .filter((ref): ref is ProductReference => ref !== undefined)
          .map((product: ProductReference, index: number) => {
            const isHovered = hoveredIndex === index;
            const showRemoved = showRemovedFn(product);
            const showAdded = showAddedFn(product);
            const displayName =
              product?.name || product?.product_number || product?.id || "";
            const isChanged = showRemoved || showAdded;

            return (
              <span
                key={`${attributeName}-prod-${index}`}
                onMouseEnter={(e) => {
                  setHoveredIndex(index);
                  const c = calcFixedCoords(e.currentTarget as HTMLElement);
                  setCoords((prev) => ({ ...prev, [index]: c }));
                }}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{ position: "relative" }}
              >
                <LinkAttr
                  to={`${adminPath}/pim/products/${
                    product?.product_number ?? product?.id
                  }`}
                >
                  {renderChip(
                    displayName,
                    isChanged,
                    column === "before",
                    theme
                  )}
                </LinkAttr>
                {isHovered && product && coords[index]
                  ? createPortal(
                      <FixedCardPreview
                        style={{
                          position: "fixed",
                          top: coords[index].top,
                          left: coords[index].left,
                        }}
                      >
                        <HistoryProductCard product={product} />
                      </FixedCardPreview>,
                      document.body
                    )
                  : null}
              </span>
            );
          })}
      </div>
    );
  }

  if (inputType === "user" && values.length > 0) {
    const extractUsers = (val: CollectionValue): User[] => {
      if (isNullOrUndefined(val)) return [];
      if (Array.isArray(val)) {
        return val
          .filter(isActivityHistoryValue)
          .map((v) => v.user)
          .filter((user): user is User => user !== undefined);
      }
      if (isActivityHistoryValue(val)) {
        return val.user ? [val.user] : [];
      }
      return [];
    };

    const beforeUsers = extractUsers(beforeValue);
    const afterUsers = extractUsers(afterValue);

    const showRemovedFn = (u: User) =>
      column === "before" && !afterUsers.some((au) => au.id === u.id);
    const showAddedFn = (u: User) =>
      column === "after" && !beforeUsers.some((bu) => bu.id === u.id);

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: spacingTokens.spacing_md,
        }}
      >
        {values
          .map((v: ActivityHistoryValue) => v.user)
          .filter((user): user is User => user !== undefined)
          .map((user: User, index: number) => {
            const showRemoved = showRemovedFn(user);
            const showAdded = showAddedFn(user);
            const displayName = getUserName(user?.firstname, user?.lastname);
            const isChanged = showRemoved || showAdded;

            return (
              <Fragment key={`${attributeName}-user-${index}`}>
                {renderChip(displayName, isChanged, column === "before", theme)}
              </Fragment>
            );
          })}
      </div>
    );
  }

  if (inputType === "object_reference" && values.length > 0) {
    const extractObjects = (val: CollectionValue): ObjectReferenceResult[] => {
      if (isNullOrUndefined(val)) return [];
      if (Array.isArray(val)) {
        return val
          .filter(isActivityHistoryValue)
          .map((v) => v.object_ref)
          .filter((ref): ref is ObjectReferenceResult => ref !== undefined);
      }
      if (isActivityHistoryValue(val)) {
        return val.object_ref ? [val.object_ref] : [];
      }
      return [];
    };

    const beforeObjects = extractObjects(beforeValue);
    const afterObjects = extractObjects(afterValue);

    const showRemovedFn = (p: ObjectReferenceResult) =>
      column === "before" && !afterObjects.some((ap) => ap.id === p.id);
    const showAddedFn = (p: ObjectReferenceResult) =>
      column === "after" && !beforeObjects.some((bp) => bp.id === p.id);

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: spacingTokens.spacing_md,
        }}
      >
        {values
          .map((v: ActivityHistoryValue) => v.object_ref)
          .filter((ref): ref is ObjectReferenceResult => ref !== undefined)
          .map((object: ObjectReferenceResult, index: number) => {
            const isHovered = hoveredIndex === index;
            const showRemoved = showRemovedFn(object);
            const showAdded = showAddedFn(object);
            const displayName =
              object?.name || object?.object_number || object?.id || "";
            const isChanged = showRemoved || showAdded;

            return (
              <span
                key={`${attributeName}-object-${index}`}
                onMouseEnter={(e) => {
                  setHoveredIndex(index);
                  const c = calcFixedCoords(e.currentTarget as HTMLElement);
                  setCoords((prev) => ({ ...prev, [index]: c }));
                }}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{ position: "relative" }}
              >
                <LinkAttr
                  to={getObjectReferenceDetailPath(
                    adminPath,
                    object.object_type,
                    object.id,
                    object.object_number
                  )}
                >
                  {renderChip(
                    displayName,
                    isChanged,
                    column === "before",
                    theme
                  )}
                </LinkAttr>
                {isHovered && object && coords[index]
                  ? createPortal(
                      <FixedCardPreview
                        style={{
                          position: "fixed",
                          top: coords[index].top,
                          left: coords[index].left,
                        }}
                      >
                        <HistoryProductCard product={object} />
                      </FixedCardPreview>,
                      document.body
                    )
                  : null}
              </span>
            );
          })}
      </div>
    );
  }

  // Single select: display as single value (not a list)
  if (inputType === "single_select" && values.length > 0) {
    const extractVariantKey = (v: ActivityHistoryValue) =>
      v.enum_variant?.id || v.enum_variant?.variant || String(v.value ?? "");

    const extractKeys = (val: CollectionValue): string[] => {
      if (isNullOrUndefined(val)) return [];
      const list = Array.isArray(val) ? val : [val];
      return list.filter(isActivityHistoryValue).map(extractVariantKey);
    };

    const beforeKeys = new Set(extractKeys(beforeValue));
    const afterKeys = new Set(extractKeys(afterValue));

    const isBeforeColumn = column === "before";
    const isAfterColumn = column === "after";

    const firstValue = values[0];
    const key = extractVariantKey(firstValue);
    const showRemoved = isBeforeColumn && !afterKeys.has(key);
    const showAdded = isAfterColumn && !beforeKeys.has(key);
    const label =
      firstValue.enum_variant?.variant || String(firstValue.value ?? "");
    const isChanged = showRemoved || showAdded;

    return <>{renderChip(label, isChanged, column === "before", theme)}</>;
  }

  // Multi select: display as list
  if (inputType === "multi_select" && values.length > 0) {
    const extractVariantKey = (v: ActivityHistoryValue) =>
      v.enum_variant?.id || v.enum_variant?.variant || String(v.value ?? "");

    const extractKeys = (val: CollectionValue): string[] => {
      if (isNullOrUndefined(val)) return [];
      const list = Array.isArray(val) ? val : [val];
      return list.filter(isActivityHistoryValue).map(extractVariantKey);
    };

    const beforeKeys = new Set(extractKeys(beforeValue));
    const afterKeys = new Set(extractKeys(afterValue));

    const isBeforeColumn = column === "before";
    const isAfterColumn = column === "after";

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: spacingTokens.spacing_md,
        }}
      >
        {values.map((v, idx) => {
          const key = extractVariantKey(v);
          const showRemoved = isBeforeColumn && !afterKeys.has(key);
          const showAdded = isAfterColumn && !beforeKeys.has(key);
          const label = v.enum_variant?.variant || String(v.value ?? "");
          const isChanged = showRemoved || showAdded;
          return (
            <Fragment key={`${attributeName}-enum-${key}-${idx}`}>
              {renderChip(label, isChanged, column === "before", theme)}
            </Fragment>
          );
        })}
      </div>
    );
  }

  if (
    (inputType === "multiline" ||
      inputType === "multiline_entry" ||
      inputType === "html") &&
    values.length > 0
  ) {
    const firstValue = values[0];
    let stringValue: string | null = null;
    if (hasValueProperty(firstValue)) {
      stringValue = isString(firstValue.value) ? firstValue.value : null;
    } else if (isString(firstValue)) {
      stringValue = firstValue;
    }
    if (isString(stringValue)) {
      const beforeHtml = extractHtmlString(beforeValue);
      const afterHtml = extractHtmlString(afterValue);
      return (
        <>{renderHtmlWithDiff(beforeHtml, afterHtml, column === "before")}</>
      );
    }
  }

  const attributeValues = values.map((v) => ({
    attribute_id: v.attribute_id,
    data_type: v.data_type,
    id: v.id,
    priority: v.priority,
    value: v.value ?? null,
    product_ref: v.product_ref ?? null,
    enum_variant: v.enum_variant ?? null,
    caption: v.caption ?? null,
    image_dimensions: v.image_dimensions ?? null,
    is_downloadable: v.is_downloadable ?? null,
    preview_url: v.preview_url ?? null,
    signed_url: v.signed_url ?? null,
    visibility: v.visibility ?? null,
    user: v.user ?? null,
    asset_name: v.asset_name ?? null,
    asset_type: v.asset_type ?? null,
    content_type: v.content_type ?? null,
    host: v.host ?? null,
  }));

  const attributeSchema: AttributeSchema = {
    id: attributeName,
    name: attributeName,
    display_name: attributeName,
    input_type: inputType as AttributeInput,
    values: attributeValues as AttributeValue[],
    description: null,
    default_values: null,
    is_system: false,
    is_master: false,
    list_name: null,
    list_id: "" as const,
    is_filterable: false,
    object_type: "product" as AttributeObjectType,
    new_object_type: "product",
    is_restricted: false,
    is_visible: true,
    is_required: false,
    is_mandatory: false,
    is_editable: false,
    translations: [],
    size: "small",
    icons_enabled: false,
  };

  return (
    <AttributeValueDisplay
      attribute={attributeSchema}
      isPortfolio={isPortfolio}
      backgroundColor={backgroundColor}
    />
  );
}

function calcFixedCoordsOfCard(element: HTMLElement | null) {
  if (!element) return { top: 0, left: 0 };
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const cardHeight = 400;
  const cardWidth = 360;
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;
  const placeAbove = spaceBelow < cardHeight && spaceAbove > spaceBelow;
  const top = placeAbove ? rect.top - cardHeight - 8 : rect.bottom + 8;
  const minLeft = 8;
  const maxLeft = Math.max(minLeft, viewportWidth - cardWidth - 8);
  const spaceRight = viewportWidth - rect.right;
  const spaceLeft = rect.left;
  const placeRight = spaceRight >= cardWidth || spaceRight >= spaceLeft;
  const desiredLeft = placeRight ? rect.right + 8 : rect.left - cardWidth - 8;
  const left = Math.min(maxLeft, Math.max(minLeft, desiredLeft));
  return { top: Math.max(8, top), left };
}
